import { memo, useState, type ReactNode } from 'react'
import { VercelAccessSettings } from './chat/ChatAccessRequests'
import { Link } from 'react-router-dom'
import { MessageSquare, History, Pencil, Sparkles, X, SlidersHorizontal, Globe, Lock, Plug, Webhook, Cpu, Gauge, Activity, Mail, TriangleAlert } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useIsAdmin } from '../hooks/useAdminMode'
import type { Project } from '../data/projectTypes'
import { useGetSecurityOwnershipQuery, useGetProjectEnvironmentsQuery, useGetProjectMembersQuery, useUpdateProjectEnvironmentMutation, useDeleteProjectEnvironmentMutation, type ProjectEnvironment } from '../services/projectDataApi'
import { useTeams } from '../hooks/useProjectTeam'
import { TEAM_COLLABORATION_PLAN_NAME } from '../lib/stripe-config'
import { useNavigate } from './nav'
import { requestChatPromptFill, setPendingChatPrompt } from '../lib/chatPrompt'
import { trackSegmentEvent } from '../lib/segment'
import { currentChatTimezone, normalizeChatTimezone } from '../lib/chatTimezone'
import { describeEnvironmentSchedule, ENVIRONMENT_SCHEDULE_DAYS, environmentScheduleCron, environmentScheduleFields } from '../lib/environmentScheduleEditor'
import { Chip, NumberRow, PolishPassesRow, TextRow, ToggleRow, useProjectPatch } from './ProjectSettingRows'
import ProjectUsageSection from './ProjectUsageSection'
import { ownershipStatusLabel } from '../schemas/securityOwnership'
import { SecurityNonceSetup } from './chat/SecurityNonceChatCard'
import ReverseProxyEvents from './ReverseProxyEvents'
import CompletionEmailConfig from './CompletionEmailConfig'
import DangerZone from './DangerZone'
import EnvironmentBadge from './EnvironmentBadge'
import './SettingsModal.css'
import './ProjectSettings.css'

// A grouped view of how the project is configured, one row per setting, editable in place. Each row
// states its current value and — for an owner or admin — opens an editor that writes through
// PATCH /api/projects/:id (see ProjectSettingRows). That is the same endpoint the chat's
// update_project_settings tool calls, so editing here and asking QA in conversation are the same
// write, with the same server-side clamping and side effects.
//
// Three kinds of row are NOT edited here:
//   • The GitHub connection, issue tracker, project members and saved logins keep a "Change in
//     chat" button. They mean multi-step auth, a provider token, or an invite — work the chat already
//     does properly, and which a text field would only half-do.
//   • A project still 'awaiting_start' (reverse-proxy onboarding) shows its status read-only: going
//     active has to pass the chat's Start QA gate, which checks the tunnel is connected first.
//   • API-customer-only delivery settings (webhooks, backend recording/log) stay read-only and hidden
//     unless configured — they're set through the API, never here or in chat.
//
// The other interactive surfaces below are unchanged: usage readout, reverse-proxy readiness, the
// completion-email send button, and the delete Danger Zone.

interface ProjectSettingsProps {
  project: Project
  // Provided when settings is rendered as a modal/panel OVER the chat layout (a chat is mounted
  // behind it): calling it closes the overlay so a "Change in chat" hand-off reveals the chat. Absent
  // on the standalone settings tab, where there is no chat — we navigate to a chat-bearing route.
  onClose?: () => void
  // 'modal' renders the account-settings shell — a sidebar of sections on the left, one section's
  // rows on the right, title and close in the sidebar header. 'stacked' (default) renders every
  // section in one column, for the classic settings tab and the mobile panel.
  variant?: 'stacked' | 'modal'
}

type ProjectSettingsTab =
  | 'qa' | 'environments' | 'access' | 'integrations' | 'delivery' | 'processing'
  | 'usage' | 'proxy' | 'email' | 'danger'

const TAB_META: Record<ProjectSettingsTab, { label: string; Icon: LucideIcon }> = {
  qa: { label: 'QA configuration', Icon: SlidersHorizontal },
  environments: { label: 'Environments', Icon: Globe },
  access: { label: 'Access', Icon: Lock },
  integrations: { label: 'Integrations', Icon: Plug },
  delivery: { label: 'Bug delivery', Icon: Webhook },
  processing: { label: 'Processing', Icon: Cpu },
  usage: { label: 'Usage', Icon: Gauge },
  proxy: { label: 'Reverse proxy', Icon: Activity },
  email: { label: 'Completion email', Icon: Mail },
  danger: { label: 'Danger zone', Icon: TriangleAlert },
}

function Row({ label, chip, detail, apiTag, onChange }: {
  label: string
  chip: ReactNode
  detail?: string
  apiTag?: boolean
  onChange?: () => void
}) {
  return (
    <div className="settings-row" data-testid="settings-row">
      <div className="settings-row__info">
        <span className="settings-row__label">
          {label}
          {apiTag && <span className="settings-row__api" title="Set via the API by API customers">API</span>}
        </span>
        {detail && <span className="settings-row__detail">{detail}</span>}
      </div>
      <div className="settings-row__status">
        {chip}
        {onChange && (
          <button
            type="button"
            className="settings-row__change"
            onClick={onChange}
            data-testid="settings-row-change"
            data-analytics-event="project-settings-change-in-chat"
          >
            <MessageSquare size={13} aria-hidden /> Change in chat
          </button>
        )}
      </div>
    </div>
  )
}

/**
 * Security testing, which is the one pass that cannot simply be ticked on: it sends real attack traffic,
 * so the project has to prove it owns the target first. The row therefore carries the whole state of
 * that setup, and expands into the same ownership card the setup chat uses.
 *
 * This is also where an interrupted setup is finished. Publishing the file usually needs a deploy, so
 * the common path is to opt in during onboarding, start QA anyway, and come back here afterwards — until
 * now that came back to nothing, and ticking Security in the pass list quietly did nothing at all.
 */
function SecurityRow({ project, canEdit, patch }: {
  project: Project
  canEdit: boolean
  patch: (body: Record<string, unknown>) => Promise<string | null>
}) {
  const [open, setOpen] = useState(false)
  const [enabling, setEnabling] = useState(false)
  const [enableError, setEnableError] = useState('')
  const exempt = project.security_ownership_exempt === true
  const nonce = typeof project.security_nonce === 'string' ? project.security_nonce : ''
  const configured = project.security_probing_enabled === true && (exempt || !!nonce)

  const ownership = useGetSecurityOwnershipQuery({ projectId: project.id, targetUrl: project.target_url ?? '', nonce }, {
    skip: !configured || exempt,
    refetchOnMountOrArgChange: true,
  })
  const latest = ownership.currentData?.latest
  const confirmed = !ownership.isError && latest?.verified === true
  const ownershipLabel = ownershipStatusLabel(latest, ownership.isFetching, ownership.isError)

  if (!configured) {
    return (
      <div className="settings-row settings-row--stacked" data-testid="settings-row-security">
        <div className="settings-row__info">
          <span className="settings-row__label">Security testing</span>
          <span className="settings-row__detail">
            Probe for injection, access-control and data-exposure bugs. Needs proof you own the app.
          </span>
        </div>
        <div className="settings-row__status">
          <Chip tone="muted">Off</Chip>
          {canEdit && (
            <button
              type="button"
              className="settings-row__change"
              disabled={enabling}
              onClick={() => {
                setEnabling(true)
                setEnableError('')
                void patch({
                  enabled_polish_passes: [...new Set([...(project.enabled_polish_passes ?? []), 'security'])],
                  security_probing_enabled: true,
                }).then((err) => {
                  setEnabling(false)
                  if (err) setEnableError(err)
                  else setOpen(true)
                })
              }}
              data-testid="settings-row-security-enable"
              data-analytics-event="project-settings-security-enable"
            >
              <Sparkles size={13} aria-hidden /> {enabling ? 'Setting up…' : 'Set up'}
            </button>
          )}
        </div>
        {enableError && <div className="settings-row__panel settings-row__error">{enableError}</div>}
      </div>
    )
  }

  if (exempt) {
    return (
      <div className="settings-row" data-testid="settings-row-security">
        <div className="settings-row__info">
          <span className="settings-row__label">Security testing</span>
          <span className="settings-row__detail">
            Your account is approved for security testing, so no ownership file is needed.
          </span>
        </div>
        <div className="settings-row__status"><Chip tone="ok">On</Chip></div>
      </div>
    )
  }

  return (
    <div className="settings-row settings-row--stacked" data-testid="settings-row-security">
      <div className="settings-row__info">
        <span className="settings-row__label">Security testing</span>
        <span className="settings-row__detail">
          {latest && !ownership.isError
            ? `Last checked ${new Date(latest.checked_at).toLocaleString()}. Re-checked before every run.`
            : 'Runs once the ownership file is live at the well-known path. It is re-checked before every run.'}
        </span>
      </div>
      <div className="settings-row__status">
        <Chip tone={confirmed ? 'ok' : 'muted'}>{ownershipLabel}</Chip>
        <button
          type="button"
          className="settings-row__change"
          onClick={() => setOpen((v) => !v)}
          data-testid="settings-row-security-toggle"
          data-analytics-event="project-settings-security-open"
        >
          {open ? 'Hide' : 'Show setup'}
        </button>
      </div>
      {open && (
        <div className="settings-row__panel">
          {/* Inline rather than the chat's dialog: settings is itself sometimes a modal, and the user
              already clicked a disclosure to ask for this. */}
          <SecurityNonceSetup
            key={`${project.id}:${project.target_url}:${nonce}`}
            projectId={project.id}
            targetUrl={project.target_url ?? ''}
            nonce={nonce}
            verified={confirmed}
          />
        </div>
      )}
    </div>
  )
}

/**
 * A row for something the current plan doesn't include: it still says what the feature is, and offers
 * the plan picker instead of the chat. Shown rather than hidden on purpose — someone has to be able to
 * find out a feature exists in order to want it.
 *
 * `onChange` is still offered when there is something to manage, because the plan gates GROWING a
 * guest list, not keeping one: a project that collected guests on a higher plan must not lose the way
 * to remove them.
 */
function UpgradeRow({ label, detail, chip, testId, onChange }: {
  label: string
  detail: string
  chip: ReactNode
  testId: string
  onChange?: () => void
}) {
  return (
    <div className="settings-row" data-testid={testId}>
      <div className="settings-row__info">
        <span className="settings-row__label">{label}</span>
        <span className="settings-row__detail">{detail}</span>
      </div>
      <div className="settings-row__status">
        {chip}
        {onChange && (
          <button
            type="button"
            className="settings-row__change"
            onClick={onChange}
            data-testid="settings-row-change"
            data-analytics-event="project-settings-change-in-chat"
          >
            <MessageSquare size={13} aria-hidden /> Change in chat
          </button>
        )}
        <Link
          className="settings-row__change"
          to="/settings/subscription"
          data-testid={`${testId}-upgrade`}
          data-analytics-event="project-settings-upgrade"
        >
          <Sparkles size={13} aria-hidden /> Upgrade
        </Link>
      </div>
    </div>
  )
}

// A row that navigates somewhere instead of handing a change to the chat.
function LinkRow({ label, detail, to, testId, analyticsEvent }: {
  label: string
  detail?: string
  to: string
  testId: string
  analyticsEvent: string
}) {
  return (
    <div className="settings-row" data-testid="settings-row">
      <div className="settings-row__info">
        <span className="settings-row__label">{label}</span>
        {detail && <span className="settings-row__detail">{detail}</span>}
      </div>
      <div className="settings-row__status">
        <Link className="settings-row__change" to={to} data-testid={testId} data-analytics-event={analyticsEvent}>
          <History size={13} aria-hidden /> History
        </Link>
      </div>
    </div>
  )
}

function Group({ title, children, testId }: { title: string; children: ReactNode; testId?: string }) {
  return (
    <section className="settings-group" data-testid={testId ?? 'settings-group'}>
      <h3 className="settings-group__title">{title}</h3>
      <div className="settings-group__rows">{children}</div>
    </section>
  )
}

function isSet(v: unknown): boolean {
  return typeof v === 'string' ? v.trim().length > 0 : v != null
}

function environmentTriggerLabel(environment: ProjectEnvironment): string {
  if (environment.trigger_type === 'schedule') return describeEnvironmentSchedule(environment.trigger_config)
  if (environment.trigger_type === 'github-app') return 'GitHub App'
  if (environment.trigger_type === 'github-action') return 'GitHub Action'
  return 'Manual only'
}

function EnvironmentSettingsRow({
  environment,
  projectTargetUrl,
  canEdit,
}: {
  environment: ProjectEnvironment
  projectTargetUrl: string | null
  canEdit: boolean
}) {
  const initialSchedule = environmentScheduleFields(environment.trigger_config)
  const initialTimezone = environment.trigger_type === 'schedule' ? initialSchedule.timezone : currentChatTimezone()
  const [editing, setEditing] = useState(false)
  const [triggerType, setTriggerType] = useState<ProjectEnvironment['trigger_type']>(environment.trigger_type)
  const [targetUrl, setTargetUrl] = useState(environment.target_url ?? '')
  const [days, setDays] = useState<number[]>(initialSchedule.days)
  const [time, setTime] = useState(initialSchedule.time)
  const [timezone, setTimezone] = useState(initialTimezone)
  const [error, setError] = useState<string | null>(null)
  const [updateEnvironment, updateState] = useUpdateProjectEnvironmentMutation()
  const [deleteEnvironment, deleteState] = useDeleteProjectEnvironmentMutation()
  const [confirmDelete, setConfirmDelete] = useState(false)
  const remove = async () => {
    setError(null)
    try {
      await deleteEnvironment({ projectId: environment.project_id, environmentId: environment.id }).unwrap()
    } catch {
      setError('Could not delete the environment. Please try again.')
    }
  }

  const reset = () => {
    const schedule = environmentScheduleFields(environment.trigger_config)
    setTriggerType(environment.trigger_type)
    setTargetUrl(environment.target_url ?? '')
    setDays(schedule.days)
    setTime(schedule.time)
    setTimezone(environment.trigger_type === 'schedule' ? schedule.timezone : currentChatTimezone())
    setError(null)
  }

  const save = async () => {
    let triggerConfig: Record<string, unknown> = {}
    if (triggerType === 'schedule') {
      const cron = environmentScheduleCron(days, time)
      if (!cron) { setError('Select at least one day and a valid time.'); return }
      const normalizedTimezone = normalizeChatTimezone(timezone)
      if (normalizedTimezone !== timezone.trim()) {
        setError('Enter a valid IANA timezone such as America/Los_Angeles or America/New_York.')
        return
      }
      triggerConfig = { ...environment.trigger_config, cron, timezone: normalizedTimezone }
      delete triggerConfig.cadence
    }
    setError(null)
    try {
      await updateEnvironment({
        projectId: environment.project_id,
        environmentId: environment.id,
        environment: {
          name: environment.name,
          kind: environment.kind,
          target_url: targetUrl.trim() || null,
          testing_guidelines: environment.testing_guidelines,
          environment_variables: environment.environment_variables,
          trigger_type: triggerType,
          trigger_config: triggerConfig,
        },
      }).unwrap()
      setEditing(false)
    } catch (failure) {
      const apiError = failure as { data?: { error?: string }; message?: string }
      setError(apiError.data?.error || apiError.message || 'Failed to update the environment.')
    }
  }

  const detail = [
    environment.target_url ?? `Inherits ${projectTargetUrl ?? 'project URL'}`,
    environment.testing_guidelines ? 'Custom test instructions' : null,
    environment.excluded_journey_ids.length ? `${environment.excluded_journey_ids.length} journey exclusion(s)` : null,
  ].filter(Boolean).join(' · ')

  return (
    <div className="settings-row settings-row--editable environment-settings-row" data-testid={`environment-settings-${environment.id}`}>
      <div className="settings-row__head">
        <div className="settings-row__info">
          <span className="settings-row__label">
            <EnvironmentBadge artifact={{ environment_name: environment.name, environment_kind: environment.kind }} />
          </span>
          <span className="settings-row__detail">{detail}</span>
        </div>
        <div className="settings-row__status">
          <Chip tone={environment.trigger_type === 'manual' ? 'muted' : 'ok'}>{environmentTriggerLabel(environment)}</Chip>
          {canEdit && !editing && !confirmDelete && (
            <button type="button" className="settings-row__change" onClick={() => { reset(); setEditing(true) }} data-testid="environment-settings-edit">
              <Pencil size={13} aria-hidden /> Edit
            </button>
          )}
        </div>
      </div>
      {canEdit && !editing && (
        <div className="settings-row__editor-actions">
          {confirmDelete ? <>
            <span>Delete “{environment.name}”? Its configuration will stop applying to new runs. Existing runs and results are kept.</span>
            <button type="button" className="settings-row__save" disabled={deleteState.isLoading} onClick={() => { void remove() }}>{deleteState.isLoading ? 'Deleting…' : 'Confirm delete'}</button>
            <button type="button" className="settings-row__cancel" disabled={deleteState.isLoading} onClick={() => { setConfirmDelete(false); setError(null) }}>Cancel</button>
          </> : <button type="button" className="settings-row__change" onClick={() => { setConfirmDelete(true); setError(null) }}>Delete environment</button>}
          {error && <p className="settings-row__error" role="alert">{error}</p>}
        </div>
      )}
      {editing && (
        <div className="settings-row__editor" data-testid="environment-settings-editor">
          <label className="environment-settings-field">
            <span>URL</span>
            <input className="settings-row__input" type="url" value={targetUrl} onChange={(event) => setTargetUrl(event.target.value)} placeholder={projectTargetUrl ?? 'Inherit project URL'} />
          </label>
          <label className="environment-settings-field">
            <span>Trigger</span>
            <select className="settings-row__input" value={triggerType} onChange={(event) => setTriggerType(event.target.value as ProjectEnvironment['trigger_type'])}>
              <option value="manual">Manual only</option>
              <option value="schedule">Schedule</option>
              <option value="github-app">GitHub App</option>
              <option value="github-action">GitHub Action</option>
            </select>
          </label>
          {triggerType === 'schedule' && (
            <div className="environment-schedule-editor">
              <fieldset className="environment-schedule-days">
                <legend>Run on</legend>
                <div className="environment-schedule-days__options">
                  {ENVIRONMENT_SCHEDULE_DAYS.map((day) => (
                    <label key={day.value} className="environment-schedule-day">
                      <input
                        type="checkbox"
                        aria-label={day.label}
                        checked={days.includes(day.value)}
                        onChange={(event) => setDays((current) => event.target.checked
                          ? [...current, day.value].sort((a, b) => a - b)
                          : current.filter((value) => value !== day.value))}
                      />
                      <span>{day.short}</span>
                    </label>
                  ))}
                </div>
              </fieldset>
              <div className="environment-schedule-time-fields">
                <label className="environment-settings-field">
                  <span>Time</span>
                  <input className="settings-row__input" type="time" value={time} onChange={(event) => setTime(event.target.value)} />
                </label>
                <label className="environment-settings-field environment-settings-field--timezone">
                  <span>Timezone</span>
                  <input className="settings-row__input" value={timezone} onChange={(event) => setTimezone(event.target.value)} placeholder="America/Los_Angeles" />
                </label>
              </div>
              <span className="settings-row__hint">The selected time applies to every checked day. Times follow daylight saving rules for this timezone.</span>
            </div>
          )}
          {error && <p className="settings-row__error">{error}</p>}
          <div className="settings-row__editor-actions">
            <button type="button" className="settings-row__save" disabled={updateState.isLoading} onClick={() => { void save() }}>{updateState.isLoading ? 'Saving…' : 'Save'}</button>
            <button type="button" className="settings-row__cancel" disabled={updateState.isLoading} onClick={() => { reset(); setEditing(false) }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  )
}

export default memo(function ProjectSettings({ project, onClose, variant = 'stacked' }: ProjectSettingsProps) {
  const navigate = useNavigate()
  const [tab, setTab] = useState<ProjectSettingsTab>('qa')
  const membersQuery = useGetProjectMembersQuery(project.id)
  const environmentsQuery = useGetProjectEnvironmentsQuery(project.id)
  const userRole = membersQuery.data?.role
  const members = membersQuery.data?.items ?? []
  const canEdit = userRole === 'owner' || userRole === 'admin'
  const isAdmin = useIsAdmin()

  // Whether the plan funding THIS project includes inviting guests. The per-team answer comes from
  // /api/usage; the project's own team is the one that pays, falling back to the viewer's own team for
  // a project that predates teams (in which case they are its owner, so it is the same plan).
  // Anything we can't match leaves the invite path alone and lets the server refuse — with the reason.
  const { teams } = useTeams()
  const fundingTeam = project.team_id
    ? teams.find((team) => team.id === project.team_id)
    : teams.find((team) => team.is_owner)
  const guestsIncluded = fundingTeam?.can_invite !== false

  // Hand a change intent to the chat: fill the composer (don't send) so the user completes it. If a
  // chat is mounted (modal/panel over the chat layout) fill it live and close this overlay; otherwise
  // (standalone settings tab) stash the prompt and navigate to a route that mounts the chat, which
  // consumes the stash on mount.
  const change = (prompt: string) => () => {
    setPendingChatPrompt(prompt)
    requestChatPromptFill(prompt)
    if (onClose) onClose()
    else navigate(`/projects/${project.id}/overview`)
  }
  // Only offer the hand-off to editors; everyone else sees read-only status.
  const changer = (prompt: string) => (canEdit ? change(prompt) : undefined)

  const patch = useProjectPatch(project.id)
  // Budget changes are the one setting with analytics behind them (the paused-project toast raises the
  // budget too, and reports it the same way), so a change made here lands in the same funnel.
  const saveBudget = async (next: number | null) => {
    const failure = await patch({ budget: next })
    if (!failure) {
      trackSegmentEvent('Changed Budget', {
        budget: next,
        previousBudget: project.budget,
        direction: next === null ? 'remove' : next > (project.budget ?? 0) ? 'increase' : 'decrease',
      })
    }
    return failure
  }

  const isActive = project.status === 'active'
  // Only a running or paused project can be flipped from here; see the note at the top of the file.
  const statusEditable = isActive || project.status === 'paused'
  const autoSubmit = project.auto_submit_bugs === true ? 'opened'
    : project.auto_submit_bugs === false ? 'none'
      : String(project.auto_submit_bugs || 'opened')
  const passes = project.enabled_polish_passes ?? []
  const varCount = project.global_variables?.length ?? 0
  const github = project.github
  const tracker = project.issue_tracker

  // API-only bug-delivery rows are hidden unless configured — they're set through the API by API
  // customers, never edited here or in chat. Only render the group when at least one is present.
  const anyDelivery = isSet(project.webhook_url) || isSet(project.finished_webhook_url)
    || isSet(project.backend_recording_url) || isSet(project.backend_log_url)

  const introText = canEdit
    ? 'How this project is configured. Edit settings and environment schedules here, or ask QA in the chat — both make the same change. Provider authentication, members, and logins hand off to the chat.'
    : 'How this project is configured. Ask the project owner to change a setting.'

  // Every section, in display order. `visible` mirrors the conditions the stacked list always had;
  // the modal shows one section at a time and lists only the visible ones in its sidebar.
  const sections: { id: ProjectSettingsTab; visible: boolean; content: ReactNode }[] = [
    { id: 'qa', visible: true, content: (
      <Group title="QA configuration">
        {statusEditable ? (
          <ToggleRow label="Project status"
            chip={<Chip tone={isActive ? 'ok' : 'muted'}>{isActive ? 'Active' : 'Paused'}</Chip>}
            detail={isActive ? 'Running test loops automatically.' : 'No new test runs will start.'}
            on={isActive}
            enableLabel="Resume"
            disableLabel="Pause"
            canEdit={canEdit}
            patch={(next) => patch({ action: 'update-status', status: next ? 'active' : 'paused' })}
            testId="settings-row-status" />
        ) : (
          <Row label="Project status"
            chip={<Chip tone="muted">Awaiting start</Chip>}
            detail="Connect your local computer in the chat to start QA."
            onChange={changer('Start QA for this project')} />
        )}
        <NumberRow label="Budget"
          chip={<Chip tone="accent">{project.budget == null ? 'Unlimited' : `${project.budget} credits`}</Chip>}
          detail="Credits QA may spend before the project pauses."
          value={project.budget ?? null}
          min={0} max={1_000_000} step={5} unit="credits" unlimitedLabel="No limit"
          canEdit={canEdit}
          patch={saveBudget}
          testId="settings-row-budget" />
        <PolishPassesRow
          enabled={passes}
          canEdit={canEdit}
          patch={(next) => patch({ enabled_polish_passes: next })} />
        <SecurityRow project={project} canEdit={canEdit} patch={patch} />
        <NumberRow label="Processing concurrency"
          chip={<Chip tone="accent">{project.max_containers} max</Chip>}
          detail="Containers running QA at once."
          value={project.max_containers}
          min={0} max={10} unit="containers"
          canEdit={canEdit}
          patch={(next) => patch({ max_containers: next })}
          testId="settings-row-max-containers" />
        <ToggleRow label="Parallel MCP"
          chip={<Chip tone={project.parallel_mcp_enabled ? 'ok' : 'muted'}>{project.parallel_mcp_enabled ? 'On' : 'Off'}</Chip>}
          detail="Run up to five recording-driver tasks per harness. Harness capacity scales automatically outside the project container limit."
          on={project.parallel_mcp_enabled === true}
          enableLabel="Turn on"
          disableLabel="Turn off"
          canEdit={canEdit}
          patch={(next) => patch({ parallel_mcp_enabled: next })}
          testId="settings-row-parallel-mcp" />
        <TextRow label="Design document"
          chip={<Chip tone={isSet(project.design_document) ? 'ok' : 'muted'}>{isSet(project.design_document) ? 'Provided' : 'Not set'}</Chip>}
          value={project.design_document}
          multiline
          placeholder="What is the app supposed to do? Include a test plan here if you have one."
          hint="Saving replaces the whole document. QA reads it when judging whether behavior is a bug."
          canEdit={canEdit}
          patch={(next) => patch({ design_document: next })}
          testId="settings-row-design-document" />
        <TextRow label="App URL"
          chip={<Chip tone={isSet(project.target_url) ? 'ok' : 'muted'}>{isSet(project.target_url) ? 'Set' : 'Not set'}</Chip>}
          detail={project.target_url ?? undefined}
          value={project.target_url}
          placeholder="https://app.example.com/dashboard"
          hint="The project’s default URL. Changing it sends future QA runs to the new app URL."
          canEdit={canEdit}
          patch={(next) => patch({ target_url: next })}
          testId="settings-row-target-url" />
      </Group>
    ) },
    { id: 'environments', visible: true, content: (
      <Group title="Environments" testId="project-environments-settings">
        {environmentsQuery.isLoading ? (
          <Row label="Environments" chip={<Chip tone="muted">Loading…</Chip>} />
        ) : environmentsQuery.isError ? (
          <Row label="Environments" chip={<Chip tone="muted">Unavailable</Chip>} detail="Environment settings could not be loaded." />
        ) : (environmentsQuery.data?.length ?? 0) === 0 ? (
          <Row
            label="Project defaults"
            chip={<Chip tone="muted">Legacy</Chip>}
            detail={project.target_url ?? 'No app URL set'}
            onChange={changer('Add a named environment for this project')} />
        ) : (
          <>
            {environmentsQuery.data?.map((environment) => {
              return (
                <EnvironmentSettingsRow
                  key={environment.id}
                  environment={environment}
                  projectTargetUrl={project.target_url}
                  canEdit={canEdit} />
              )
            })}
            <Row
              label="Add environment"
              chip={<Chip tone="accent">Chat</Chip>}
              detail="Add production, staging, development, preview, or local configuration."
              onChange={changer('Add a new environment to this project')} />
          </>
        )}
      </Group>
    ) },
    { id: 'access', visible: true, content: (
      <Group title="Access">
        <ToggleRow label="Public access"
          chip={<Chip tone={project.is_public ? 'ok' : 'muted'}>{project.is_public ? 'Public' : 'Private'}</Chip>}
          detail={project.is_public ? 'Anyone with the link can view.' : 'Only members can view.'}
          on={project.is_public === true}
          enableLabel="Make public"
          disableLabel="Make private"
          canEdit={canEdit}
          patch={(next) => patch({ is_public: next })}
          testId="settings-row-public" />
        {/* "Guests", not "Members": people invited to THIS project only. Everyone in the team that
            owns the project already has access without appearing here (Settings › Team is that list),
            so the two layers need names that can't be mistaken for each other. */}
        {guestsIncluded ? (
          <Row label="Project guests"
            chip={<Chip tone="accent">{members.length || '—'}</Chip>}
            detail={members.map((m) => m.email || m.user_id).slice(0, 4).join(', ') || undefined}
            onChange={changer('Add or remove a project guest')} />
        ) : (
          <UpgradeRow label="Project guests"
            chip={<Chip tone="muted">{TEAM_COLLABORATION_PLAN_NAME} plan</Chip>}
            detail={members.length
              ? `${members.length} already — inviting more needs ${TEAM_COLLABORATION_PLAN_NAME}.`
              : `Invite people to this project on ${TEAM_COLLABORATION_PLAN_NAME} and above.`}
            testId="settings-row-guests-upgrade"
            onChange={members.length ? changer('Remove a project guest') : undefined} />
        )}
        <TextRow label="Share location"
          chip={<Chip tone={isSet(project.share_location) ? 'ok' : 'muted'}>{isSet(project.share_location) ? 'Set' : 'Not set'}</Chip>}
          detail={project.share_location ?? undefined}
          value={project.share_location}
          placeholder="https://x.com/you/status/123"
          hint="Link to a public post about the app. Adding one makes the project public and refunds the credits it has spent."
          canEdit={canEdit}
          patch={(next) => patch({ share_location: next })}
          testId="settings-row-share-location" />
      </Group>
    ) },
    { id: 'integrations', visible: true, content: (
      <Group title="Integrations">
        <VercelAccessSettings projectId={project.id} canEdit={canEdit} />
        <Row label="GitHub"
          chip={<Chip tone={github?.repoUrl ? 'ok' : 'muted'}>{github?.repoUrl ? 'Connected' : 'Not connected'}</Chip>}
          detail={github?.repoUrl || undefined}
          onChange={changer(github?.repoUrl ? 'Manage the GitHub connection' : 'Connect this project to GitHub')} />
        <Row label="Issue tracker"
          chip={<Chip tone={tracker?.enabled ? 'ok' : 'muted'}>{tracker ? `${tracker.provider}${tracker.enabled ? '' : ' (off)'}` : 'Not configured'}</Chip>}
          detail="Where QA files discovered bugs."
          onChange={changer(tracker ? 'Change the issue tracker where bugs are filed' : 'Set up an issue tracker (GitHub, GitLab, Linear, or Jira) for filing bugs')} />
        <Row label="Logins & variables"
          chip={<Chip tone={varCount ? 'ok' : 'muted'}>{varCount ? `${varCount} set` : 'None'}</Chip>}
          onChange={changer('Add or update login credentials')} />
      </Group>
    ) },
    { id: 'delivery', visible: anyDelivery, content: (
        <Group title="Bug delivery">
          {isSet(project.webhook_url) && (
            <Row label="Bug-report webhook" apiTag chip={<Chip tone="ok">Configured</Chip>} detail={project.webhook_url ?? undefined} />
          )}
          {isSet(project.webhook_url) && (
            <Row label="Auto-submit mode" apiTag chip={<Chip tone="accent">{autoSubmit}</Chip>} detail="Governs the webhook only." />
          )}
          {isSet(project.finished_webhook_url) && (
            <Row label="QA-finished webhook" apiTag chip={<Chip tone="ok">Configured</Chip>} detail={project.finished_webhook_url ?? undefined} />
          )}
          {isSet(project.backend_recording_url) && (
            <Row label="Backend request recording" apiTag chip={<Chip tone="ok">Configured</Chip>} />
          )}
          {isSet(project.backend_log_url) && (
            <Row label="Backend log fetcher" apiTag chip={<Chip tone="ok">Configured</Chip>} />
          )}
        </Group>
    ) },
    // Task execution detail — which container ran what, and when. Admin-only tooling, matching the
    // processing-history page and its endpoint.
    { id: 'processing', visible: isAdmin, content: (
        <Group title="Processing" testId="project-processing">
          <LinkRow
            label="Processing history"
            detail="Completed and failed runs for this project."
            to={`/projects/${project.id}/processing-history`}
            testId="project-processing-history-link"
            analyticsEvent="project-settings-processing-history" />
        </Group>
    ) },
    { id: 'usage', visible: true, content: <ProjectUsageSection project={project} /> },
    { id: 'proxy', visible: project.use_reverse_proxy === true, content: <ReverseProxyEvents project={project} /> },
    { id: 'email', visible: true, content: <CompletionEmailConfig project={project} /> },
    { id: 'danger', visible: canEdit, content: <DangerZone project={project} /> },
  ]
  const visibleSections = sections.filter((section) => section.visible)

  if (variant === 'modal') {
    const active = visibleSections.find((section) => section.id === tab) ?? visibleSections[0]!
    return (
      <div
        className="user-settings-modal project-settings-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="project-settings-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="user-settings-modal__sidebar">
          <div className="user-settings-modal__sidebar-header">
            <span id="project-settings-modal-title" className="user-settings-modal__title">Project settings</span>
            <button
              type="button"
              className="user-settings-modal__close"
              onClick={onClose}
              aria-label="Close project settings"
              data-testid="project-settings-modal-close"
              data-analytics-event="project-settings-close-button"
            >
              <X size={18} aria-hidden />
            </button>
          </div>
          <nav className="user-settings-modal__nav">
            {visibleSections.map(({ id }) => {
              const { label, Icon } = TAB_META[id]
              return (
                <button
                  key={id}
                  type="button"
                  className={`user-settings-modal__nav-item${active.id === id ? ' user-settings-modal__nav-item--active' : ''}${id === 'danger' ? ' user-settings-modal__nav-item--danger' : ''}`}
                  onClick={() => setTab(id)}
                  data-testid={`project-settings-tab-${id}`}
                  data-analytics-event={`project-settings-tab-${id}`}
                >
                  <Icon size={16} strokeWidth={1.75} aria-hidden />
                  {label}
                </button>
              )
            })}
          </nav>
        </div>
        <div className="user-settings-modal__content project-settings project-settings--modal" data-testid="project-settings">
          {active.id === 'qa' && <p className="project-settings__intro">{introText}</p>}
          {active.content}
        </div>
      </div>
    )
  }

  return (
    <div className="project-settings" data-testid="project-settings">
      <p className="project-settings__intro">{introText}</p>
      {visibleSections.map((section) => <div key={section.id} className="project-settings__stack-item">{section.content}</div>)}
    </div>
  )
})
